const crypto = require('crypto');

// 黑名单头部（不参与签名）
const UNSIGNABLE_HEADERS = [
    'authorization',
    'content-type',
    'content-length',
    'user-agent',
    'presigned-expires',
    'expect',
    'x-amzn-trace-id'
];

// SHA256 hash
function sha256(data) {
    return crypto.createHash('sha256').update(data, 'utf8').digest('hex');
}

// HMAC SHA256
function hmac(key, data) {
    return crypto.createHmac('sha256', key).update(data, 'utf8').digest();
}

// 获取签名密钥
function getSigningKey(secretAccessKey, dateStamp, region, service) {
    const kDate = hmac('AWS4' + secretAccessKey, dateStamp);
    const kRegion = hmac(kDate, region);
    const kService = hmac(kRegion, service);
    const kSigning = hmac(kService, 'aws4_request');
    return kSigning;
}

// 判断头部是否可签名
function isSignableHeader(header) {
    const lowerHeader = header.toLowerCase();
    return lowerHeader.startsWith('x-amz-') || !UNSIGNABLE_HEADERS.includes(lowerHeader);
}

// 规范化头部值（移除多余空格）
function canonicalHeaderValues(value) {
    return value.replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
}

// 生成 Canonical Headers
function canonicalHeaders(headers) {
    const headerPairs = [];
    
    Object.keys(headers).forEach(key => {
        headerPairs.push([key, headers[key]]);
    });
    
    // 按头部名称排序
    headerPairs.sort((a, b) => {
        return a[0].toLowerCase() < b[0].toLowerCase() ? -1 : 1;
    });
    
    const result = [];
    headerPairs.forEach(([key, value]) => {
        const lowerKey = key.toLowerCase();
        if (isSignableHeader(lowerKey)) {
            result.push(`${lowerKey}:${canonicalHeaderValues(value.toString())}`);
        }
    });
    
    return result.join('\n');
}

// 生成 Signed Headers
function signedHeaders(headers) {
    const headerNames = [];
    
    Object.keys(headers).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (isSignableHeader(lowerKey)) {
            headerNames.push(lowerKey);
        }
    });
    
    return headerNames.sort().join(';');
}

// 生成 Canonical Query String
export function canonicalQueryString(params) {
    if (!params || Object.keys(params).length === 0) {
        return '';
    }
    
    return Object.keys(params)
        .sort()
        .map(key => {
            const value = params[key];
            return encodeURIComponent(key) + '=' + encodeURIComponent(String(value));
        })
        .join('&');
}

// ISO8601 时间格式
function iso8601(date) {
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// 生成随机字符串
export function randomS() {
    const pool = '0123456789abcdefghijklmnopqrstuvwxyz';
    let s = '';
    for (let i = 0; i < 11; i++) {
        s += pool.charAt(Math.floor(Math.random() * pool.length));
    }
    return s;
}

// 主函数：生成 Authorization
export function generateAuthorization(config) {
    const {
        accessKeyId,
        secretAccessKey,
        sessionToken,
        region,
        service,
        method = 'GET',
        pathname = '/',
        params = {},
        body = '',
        headers = {}
    } = config;
    
    // 1. 准备时间
    const now = new Date();
    const amzDate = iso8601(now).replace(/[:\-]|\.\d{3}/g, '');
    const dateStamp = amzDate.substr(0, 8);
    
    // 2. 准备请求头
    const requestHeaders = {
        ...headers,
        'X-Amz-Date': amzDate
    };
    
    if (sessionToken) {
        requestHeaders['x-amz-security-token'] = sessionToken;
    }
    
    // 如果有 body，添加 content hash
    if (body) {
        requestHeaders['X-Amz-Content-Sha256'] = sha256(JSON.stringify(body));
    }
    
    // 3. 生成 Canonical Request
    const canonicalQueryStr = canonicalQueryString(params);
    const canonicalHeadersStr = canonicalHeaders(requestHeaders);
    const signedHeadersStr = signedHeaders(requestHeaders);
    const payloadHash = body ? sha256(JSON.stringify(body)) : sha256('');
    
    const canonicalRequest = [
        method.toUpperCase(),
        pathname,
        canonicalQueryStr,
        canonicalHeadersStr + '\n',
        signedHeadersStr,
        payloadHash
    ].join('\n');
    
    // console.log('=== Canonical Request ===');
    // console.log(canonicalRequest);
    // console.log('========================\n');
    
    // 4. 生成 String to Sign
    const algorithm = 'AWS4-HMAC-SHA256';
    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const canonicalRequestHash = sha256(canonicalRequest);
    
    const stringToSign = [
        algorithm,
        amzDate,
        credentialScope,
        canonicalRequestHash
    ].join('\n');
    
    // console.log('=== String to Sign ===');
    // console.log(stringToSign);
    // console.log('======================\n');
    
    // 5. 计算签名
    const signingKey = getSigningKey(secretAccessKey, dateStamp, region, service);
    const signature = crypto.createHmac('sha256', signingKey)
        .update(stringToSign, 'utf8')
        .digest('hex');
    
    // console.log('=== Signature ===');
    // console.log(signature);
    // console.log('=================\n');
    
    // 6. 生成 Authorization 头
    const authorization = `${algorithm} Credential=${accessKeyId}/${credentialScope}, SignedHeaders=${signedHeadersStr}, Signature=${signature}`;
    
    return {
        authorization,
        headers: requestHeaders,
        amzDate
    };
}
const fs = require('fs');

export function calculateFileCrc32(filePath) {
    const data = fs.readFileSync(filePath);
    const crc32Value = crc32(data);
    const crc32Hex = crc32Value.toString(16);
    return crc32Hex;
}

function crc32(data) {
    const CRC_TABLE = [];
    for (let i = 0; i < 256; i++) {
        let crc = i;
        for (let j = 0; j < 8; j++) {
            crc = (crc & 1) ? (0xEDB88320 ^ (crc >>> 1)) : (crc >>> 1);
        }
        CRC_TABLE.push(crc >>> 0);
    }

    let crc = 0xFFFFFFFF;
    for (let i = 0; i < data.length; i++) {
        const byte = data[i];
        const index = (crc ^ byte) & 0xFF;
        crc = (CRC_TABLE[index] ^ (crc >>> 8)) >>> 0;
    }

    crc = (crc ^ 0xFFFFFFFF) >>> 0;
    return crc;
}

